import React from 'react';
import { DocumentAttachment } from '../types';
import { FileText, Image, Download, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DocumentViewerProps {
  documents: DocumentAttachment[];
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documents }) => {
  if (!documents || documents.length === 0) {
    return null;
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocumentIcon = (type: string) => {
    return type === 'pdf' ? 
      <FileText size={20} className="text-red-500" /> : 
      <Image size={20} className="text-navy-500" />;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getSignedUrl = async (doc: DocumentAttachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('mbfcsessionplans')
        .createSignedUrl(doc.url, 3600); // 1 hour expiry

      if (error) {
        throw error;
      }

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Error getting signed URL:', err);
      alert('Failed to open document. Please try again.');
    }
  };

  const downloadDocument = async (doc: DocumentAttachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('mbfcsessionplans')
        .download(doc.url);

      if (error) {
        throw error;
      }

      // Create a download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document. Please try again.');
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <ul className="space-y-2">
        {documents.map(doc => (
          <li key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-200">
            <div className="flex items-center">
              {getDocumentIcon(doc.type)}
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-800">{doc.name}</p>
                <div className="flex items-center text-xs text-gray-500">
                  <span>{formatFileSize(doc.size)}</span>
                  <span className="mx-2">•</span>
                  <span>Added {formatDate(doc.uploadedAt)}</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => getSignedUrl(doc)}
                className="text-navy-600 hover:text-navy-800 p-1"
                aria-label="View document"
              >
                <ExternalLink size={18} />
              </button>
              <button
                onClick={() => downloadDocument(doc)}
                className="text-navy-600 hover:text-navy-800 p-1"
                aria-label="Download document"
              >
                <Download size={18} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DocumentViewer;